import { fromSeoUrl } from 'helpers/HelperFunctions';
import {
  EXPERIENCE_LEVEL_OPTIONS,
  isExperienceGroupChecked,
} from 'components/JobsFilterPanel/experienceLevelOptions';
import {
  isRoleSelected,
  resolveSelectedRole,
} from 'components/JobsFilterPanel/roleFilterUtils';

/**
 * Build removable empty-state chips for applied narrowing filters.
 * Order: search, roles, location, Remote/Hybrid/On-site, experience, type, Easy apply.
 * "Open to applications" and Newest-first are never chips.
 *
 * @param {object} filter - ProjectsBase filter state
 * @param {object} [options]
 * @param {Array} [options.suggestedRoles]
 * @returns {Array<{ key: string, label: string, type: string, payload?: any }>}
 */
export default function buildJobsEmptyFilterChips(
  filter,
  { suggestedRoles = [] } = {}
) {
  if (!filter) return [];

  const chips = [];

  if (filter.searchTerm) {
    const term = String(filter.searchTerm);
    chips.push({
      key: 'search',
      type: 'search',
      label: `"${term}"`,
      payload: null,
    });
  }

  (filter.selectedRoles || []).forEach((selected) => {
    const resolved = resolveSelectedRole(selected, suggestedRoles);
    chips.push({
      key: `role:${resolved.slug || resolved.name}`,
      type: 'role',
      label: resolved.name,
      payload: { roleName: resolved.name, selected },
    });
  });

  if (filter.locationText) {
    chips.push({
      key: 'location',
      type: 'location',
      label: fromSeoUrl(filter.locationText),
      payload: null,
    });
  }

  if (filter.remote) {
    chips.push({
      key: 'remote',
      type: 'remote',
      label: 'Remote',
      payload: null,
    });
  }

  // Hybrid / On-site: reserved for when those filters exist in the column.

  EXPERIENCE_LEVEL_OPTIONS.forEach((option) => {
    if (isExperienceGroupChecked(filter.experienceLevels, option)) {
      chips.push({
        key: `experience:${option.label}`,
        type: 'experience',
        label: option.label,
        payload: { option },
      });
    }
  });

  (filter.hiringCapacities || [])
    .slice()
    .sort((a, b) => {
      if (a.type === b.type) return 0;
      if (a.type === 'commercial') return -1;
      if (b.type === 'commercial') return 1;
      return 0;
    })
    .forEach((capacity) => {
      if (!capacity.isChecked) return;
      chips.push({
        key: `type:${capacity.type}`,
        type: 'type',
        label: capacity.label,
        payload: { capacity },
      });
    });

  if (filter.internal) {
    chips.push({
      key: 'internal',
      type: 'internal',
      label: 'Easy apply',
      payload: null,
    });
  }

  return chips;
}

/**
 * True when every chip is only the page's own role and/or country/city
 * (empty role/country page → "No jobs here yet", no chip row).
 */
export function chipsAreOnlyPageIdentity(
  chips,
  { pageRole = null, pageCountry = null, pageCity = null } = {}
) {
  if (!chips || chips.length === 0) return true;

  return chips.every((chip) => {
    if (chip.type === 'role' && pageRole) {
      return isRoleSelected(
        [pageRole],
        chip.payload.roleName,
        chip.payload.selected
      );
    }
    if (chip.type === 'location' && pageCountry) {
      const pageLocationText = pageCity
        ? `${pageCity}, ${pageCountry}`
        : pageCountry;
      return (
        fromSeoUrl(pageLocationText) === chip.label ||
        pageLocationText === chip.label
      );
    }
    return false;
  });
}
